import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, Loader2, Edit, Trash2, Copy, FileText, ExternalLink, Upload } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { ProductEditModal } from './ProductEditModal';
import { UploadDocumentModal } from './UploadDocumentModal';
import { documentService } from '../../../../services/documentos/documentService';

export const ProductDetails = ({ product, loading, onDelete, productTypes = [], onSaveUpdate, onSaveDuplicate, onDocumentUploaded }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editMode, setEditMode] = useState('edit'); // 'edit' | 'duplicate'

  const { data: documentFeatures } = useQuery({
    queryKey: ['documentFeatures'],
    queryFn: () => documentService.getDocumentFeatures(),
    enabled: isUploadModalOpen,
  });
  const documentTypes = documentFeatures?.document_types ?? [];

  const getDocumentTitle = (doc, index, fallbackPrefix = 'Document') => {
    if (doc?.Name && doc.Name.trim().length > 0) {
      return doc.Name;
    }
    if (doc?.Description && doc.Description.trim().length > 0) {
      return doc.Description;
    }
    return `${fallbackPrefix} ${index + 1}`;
  };
  const handleEditClick = () => {
    setEditMode('edit');
    setIsEditModalOpen(true);
  };

  const handleDuplicateClick = () => {
    setEditMode('duplicate');
    setIsDuplicateModalOpen(true);
  };

  const handleModalClose = () => {
    setIsEditModalOpen(false);
    setIsDuplicateModalOpen(false);
  };

  const handleSave = async (productData) => {
    if (editMode === 'duplicate') {
      await onSaveDuplicate(productData);
    } else {
      await onSaveUpdate(productData);
    }
    handleModalClose();
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-8 min-h-0 max-h-[700px] flex items-center justify-center">
        <div className="flex flex-col items-center text-gray-400">
          <Loader2 className="animate-spin mb-4" size={32} />
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-8 min-h-0 max-h-[700px] flex items-center justify-center">
        <div className="text-center text-gray-400">
          <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Select a product to view its details</p>
        </div>
      </div>
    );
  }

  const featureValues = product.feature_values || {};

  return (
    <>
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg flex flex-col h-full min-h-0 max-h-[700px]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-gray-400" />
            <h2 className="text-xl font-semibold text-white">
              Product Details
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {product && (
              <button
                type="button"
                onClick={handleEditClick}
                className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-blue-400 transition-colors"
                title="Edit product"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            {product && (
              <button
                type="button"
                onClick={handleDuplicateClick}
                className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-green-400 transition-colors"
                title="Duplicate product"
              >
                <Copy className="w-4 h-4" />
              </button>
            )}
            {product && (
              <button
                type="button"
                onClick={() => setIsUploadModalOpen(true)}
                className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-blue-400 transition-colors"
                title="Upload document"
              >
                <Upload className="w-4 h-4" />
              </button>
            )}
            {product && onDelete && (
              <button
                type="button"
                onClick={() => onDelete(product.id)}
                className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-red-400 transition-colors"
                title="Delete product"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <section>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-gray-400" />
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Name</p>
                  <p className="text-white font-medium">{product.name || 'N/A'}</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Product Type</p>
                  <Badge className="bg-violet-900/50 text-violet-200">
                    {product.product_type || 'N/A'}
                  </Badge>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Manufacturer</p>
                  <p className="text-white">{product.manufacturer || 'N/A'}</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Family</p>
                  <p className="text-white">{product.family || 'N/A'}</p>
                </div>
                {product.description && (
                  <div className="bg-gray-700/50 rounded-lg p-4 md:col-span-2">
                    <p className="text-xs text-gray-400 mb-1">Description</p>
                    <p className="text-white whitespace-pre-wrap">{product.description}</p>
                  </div>
                )}
              </div>
            </section>

            {/* Feature Values */}
            {Object.keys(featureValues).length > 0 && (
              <section>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-gray-400" />
                  Product Features
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(featureValues).map(([featureName, featureValue]) => (
                    <div key={featureName} className="bg-gray-700/50 rounded-lg p-4">
                      <p className="text-xs text-gray-400 mb-1">{featureName}</p>
                      <p className="text-white">
                        {featureValue !== null && featureValue !== undefined && featureValue !== ''
                          ? String(featureValue)
                          : 'N/A'}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Documents */}
            {(product.documents?.length > 0 || product.familyDocuments?.length > 0) && (
              <section>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-400" />
                  Documents
                </h3>
                <div className="space-y-4">
                  {/* Documentos del producto */}
                  {product.documents?.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-400 mb-2">Product Documents</p>
                      <div className="space-y-2">
                        {product.documents.map((doc, idx) => (
                          <a
                            key={doc.ID}
                            href={documentService.getDocumentContentUrl(doc.ID)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors group"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="text-white font-medium group-hover:text-blue-400 transition-colors truncate">
                                  {getDocumentTitle(doc, idx, 'Product document')}
                                </div>
                                {doc.Description && (
                                  <div className="text-sm text-gray-400 mt-1 line-clamp-2">{doc.Description}</div>
                                )}
                              </div>
                              <ExternalLink size={20} className="text-gray-500 group-hover:text-blue-400 transition-colors flex-shrink-0 ml-2" />
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Documentos de la familia */}
                  {product.familyDocuments?.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-400 mb-2">Family Documents</p>
                      <div className="space-y-2">
                        {product.familyDocuments.map((doc, idx) => (
                          <a
                            key={doc.ID}
                            href={documentService.getDocumentContentUrl(doc.ID)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors group"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="text-white font-medium group-hover:text-blue-400 transition-colors truncate">
                                  {getDocumentTitle(doc, idx, 'Family document')}
                                </div>
                                {doc.Description && (
                                  <div className="text-sm text-gray-400 mt-1 line-clamp-2">{doc.Description}</div>
                                )}
                              </div>
                              <ExternalLink size={20} className="text-gray-500 group-hover:text-blue-400 transition-colors flex-shrink-0 ml-2" />
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      {/* Edit/Duplicate Modal */}
      {(isEditModalOpen || isDuplicateModalOpen) && (
        <ProductEditModal
          isOpen={isEditModalOpen || isDuplicateModalOpen}
          onClose={handleModalClose}
          product={product}
          productTypes={productTypes}
          mode={editMode}
          onSave={handleSave}
        />
      )}

      {/* Upload Document Modal */}
      {isUploadModalOpen && (
        <UploadDocumentModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          product={product}
          documentTypes={documentTypes}
          onSuccess={onDocumentUploaded}
        />
      )}
    </>
  );
};
